import { Type, ShapeTypeEnumType, Position, Icon, PlaceTypeEnumType, } from '@wix/ambassador-seating-v1-seating-plan/types';
import { isDarkColor } from '@wix/panda-js-utils';
import { COLORS } from '../constants';
import { IconType, LabelsPosition, SHAPE, } from '../interfaces';
import { degreesToRadians } from './utils';
import { getNumberingToSequence } from './plan';
import { createLabelGenerator } from './label-generator';
const getCategoriesColorsMap = (categories) => {
    const categoriesColorsMap = {};
    categories.forEach(category => {
        categoriesColorsMap[category.id] = category.config.color;
    });
    return categoriesColorsMap;
};
export const elementsToShapes = ({ plan, shouldUseCategories, onlyShapeIds }) => {
    const { categories, sections } = plan;
    const categoriesColorsMap = shouldUseCategories ? getCategoriesColorsMap(categories) : {};
    const categoryProps = (element) => {
        if (shouldUseCategories) {
            const categoryColor = categoriesColorsMap[element.categoryId];
            return {
                categoryId: element.categoryId,
                shapeColor: categoryColor,
                shapeTextColor: isDarkColor(categoryColor) ? COLORS.WHITE : COLORS.TEXT,
            };
        }
        return {
            categoryId: element.categoryId,
        };
    };
    const include = (elementId) => (onlyShapeIds ? onlyShapeIds.includes(elementId) : true);
    return sections
        .flatMap((section) => section.elements.map((element) => include(element.id)
        ? {
            ...getElementConfig(element),
            ...categoryProps(element),
            label: element.title,
            type: getType(element),
            capacity: element.capacity,
            startAt: element.sequencing?.startAt?.toString(),
            reverseDirection: element.sequencing?.reverseOrder,
            id: element.id,
            sectorLabel: section.default ? '' : section.title,
            reservationOptions: element.reservationOptions,
            groupId: element.elementGroupId,
            ...(element.places?.length
                ? {
                    places: element.places.map(place => ({
                        ...place,
                        type: place.type ?? PlaceTypeEnumType.STANDARD,
                    })),
                }
                : {}),
            ...(element.overrides?.length ? { placeOverrides: element.overrides } : {}),
            ...getMultiRowProps(element),
        }
        : null))
        .filter(a => a);
};
export const getElementConfig = (element) => {
    const { x, y, zIndex, width, height, rotationAngle, fontSize, cornerRadius, seatSpacing, hideLabel, labelPosition, seatLayout, emptyTopSeatSpaces, text, color, fillColor, strokeColor, strokeWidth, opacity, icon, image, seatNumbering, } = element.uiProperties;
    const config = {
        x,
        y,
        sortId: zIndex,
        width,
        height,
        angle: degreesToRadians(rotationAngle),
        fontSize,
        cornerRadius,
        spacing: seatSpacing,
        hideLabel,
        labelPosition: getShapeLabelPosition(labelPosition),
        layout: seatLayout,
        empty: emptyTopSeatSpaces,
        text,
        color,
        fillColor,
        strokeColor,
        strokeWidth,
        opacity: opacity / 100,
        icon: getShapeIcon(icon),
        image: getShapeImage(image),
        sequence: getNumberingToSequence(seatNumbering),
    };
    return config;
};
export const getType = (element) => {
    switch (element.type) {
        case Type.AREA:
            return SHAPE.AREA;
        case Type.ROW:
            return SHAPE.ROW;
        case Type.ROUND_TABLE:
            return SHAPE.ROUND_TABLE;
        case Type.TABLE:
            return SHAPE.TABLE;
        case Type.SHAPE:
            return getShapeType(element.uiProperties?.shapeType);
        case Type.MULTI_ROW:
            return SHAPE.MULTI_ROW;
        default:
            return undefined;
    }
};
export const getShapeLabelPosition = (labelPosition) => {
    const map = {
        [Position.LEFT]: LabelsPosition.LEFT,
        [Position.RIGHT]: LabelsPosition.RIGHT,
        [Position.BOTH]: LabelsPosition.BOTH,
    };
    return map[labelPosition] ?? LabelsPosition.NONE;
};
export const getShapeType = (shapeType) => {
    const map = {
        [ShapeTypeEnumType.ELLIPSE]: SHAPE.CIRCLE,
        [ShapeTypeEnumType.ICON]: SHAPE.ICON,
        [ShapeTypeEnumType.IMAGE]: SHAPE.IMAGE,
        [ShapeTypeEnumType.LINE]: SHAPE.LINE,
        [ShapeTypeEnumType.RECTANGLE]: SHAPE.RECTANGLE,
        [ShapeTypeEnumType.TEXT]: SHAPE.TEXT,
    };
    return map[shapeType] ?? undefined;
};
// TODO: start using ambassador types once migrated out of config
export const getShapeIcon = (icon) => {
    const map = {
        [Icon.CHECKROOM]: IconType.checkroom,
        [Icon.DRINKS]: IconType.drinks,
        [Icon.ELEVATOR]: IconType.elevator,
        [Icon.ENTER]: IconType.enter,
        [Icon.EXIT]: IconType.exit,
        [Icon.FOOD]: IconType.food,
        [Icon.SMOKING]: IconType.smoking,
        [Icon.STAGE]: IconType.stage,
        [Icon.STAIRS]: IconType.stairs,
        [Icon.WC]: IconType.wc,
        [Icon.WC_MEN]: IconType.wcMen,
        [Icon.WC_WOMEN]: IconType.wcWomen,
    };
    return map[icon] ?? undefined;
};
export const getShapeImage = (image) => {
    if (!image) {
        return undefined;
    }
    return {
        id: image.id,
        originalWidth: image.width,
        originalHeight: image.height,
        uri: image.uri,
    };
};
const getMultiRowProps = (element) => {
    if (element.multiRowProperties) {
        const { verticalSequencing: { startAt, reverseOrder, rowNumbering }, rowSpacing, } = element.multiRowProperties;
        const rows = getMultiRowRows(element);
        return {
            rowsStartAt: startAt,
            rowsReverseDirection: reverseOrder,
            spacingBetweenRows: rowSpacing,
            rowsSequence: getNumberingToSequence(rowNumbering),
            rows,
            // rows contain multirow place overrides
            placeOverrides: [],
        };
    }
    else {
        return {};
    }
};
const getMultiRowRows = (element) => {
    const rowLabelGenerator = createLabelGenerator({
        sequence: getNumberingToSequence(element.multiRowProperties.verticalSequencing.rowNumbering),
        startAt: element.multiRowProperties.verticalSequencing.startAt,
        reverseDirection: element.multiRowProperties.verticalSequencing.reverseOrder,
        capacity: element.multiRowProperties.rows.length,
    });
    let placeOverrideIndexOffset = 0;
    const rows = element.multiRowProperties.rows.map((elementRow, rowIndex) => {
        const overrides = { width: elementRow.uiProperties.width };
        if (elementRow.uiProperties.relativeX) {
            overrides.relativeX = elementRow.uiProperties.relativeX;
        }
        const defaultLabel = rowLabelGenerator.getLabelByIndex(rowIndex);
        if (elementRow.title !== defaultLabel) {
            overrides.label = elementRow.title;
        }
        if (elementRow.sequencing.startAt !== element.sequencing.startAt) {
            overrides.startAt = elementRow.sequencing.startAt;
        }
        if (elementRow.sequencing.reverseOrder !== element.sequencing.reverseOrder) {
            overrides.reverseDirection = elementRow.sequencing.reverseOrder;
        }
        if (elementRow.uiProperties.seatSpacing !== element.uiProperties.seatSpacing) {
            overrides.spacing = elementRow.uiProperties.seatSpacing;
        }
        if (elementRow.uiProperties.labelPosition !== element.uiProperties.labelPosition) {
            overrides.labelPosition = getShapeLabelPosition(elementRow.uiProperties.labelPosition);
        }
        if (elementRow.uiProperties.seatNumbering !== element.uiProperties.seatNumbering) {
            overrides.sequence = getNumberingToSequence(elementRow.uiProperties.seatNumbering);
        }
        const rowPlaceOverrides = (element.overrides ?? [])
            .filter(placeOverride => placeOverride.index >= placeOverrideIndexOffset &&
            placeOverride.index < placeOverrideIndexOffset + elementRow.capacity)
            .map(placeOverride => ({ ...placeOverride, index: placeOverride.index - placeOverrideIndexOffset }));
        placeOverrideIndexOffset += elementRow.capacity;
        return {
            id: elementRow.id,
            overrides,
            placeOverrides: rowPlaceOverrides,
        };
    });
    return rows;
};
//# sourceMappingURL=elements-to-shapes.js.map